
import { useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ChatInputProps {
  onSendMessage: (content: string) => void;
  disabled?: boolean;
}

export const ChatInput = ({ onSendMessage, disabled = false }: ChatInputProps) => {
  const [inputValue, setInputValue] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !disabled) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Example complex prompts users might want to use
  const examplePrompts = [
    "Analyze our best-performing candidates",
    "Draft an email to follow up with the client",
    "Compare our client metrics year-over-year",
    "Prioritize my outstanding tasks"
  ];

  const handleUsePrompt = (prompt: string) => {
    setInputValue(prompt);
  };

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex items-center gap-2">
          <Textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message here..."
            className="min-h-[60px] resize-none"
            disabled={disabled}
          />
          <Button 
            type="submit" 
            size="icon" 
            disabled={!inputValue.trim() || disabled} 
            className="h-[60px] w-[60px] rounded-full"
          >
            <Send className="h-5 w-5" />
            <span className="sr-only">Send message</span>
          </Button>
        </div>

        {!inputValue.trim() && (
          <div className="flex items-center gap-2 overflow-x-auto py-2 scrollbar-thin">
            <span className="text-sm text-muted-foreground flex-shrink-0 flex items-center">
              <Sparkles className="h-3 w-3 mr-1" />
              Try:
            </span>
            {examplePrompts.map((prompt) => (
              <TooltipProvider key={prompt}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs whitespace-nowrap"
                      onClick={() => handleUsePrompt(prompt)}
                    >
                      {prompt}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-xs">Click to use this prompt</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};
